import { RuntimeContext } from "./NGFieldExtensions";
import { callServiceLight, getState } from "./dataService";
import { getApiToken } from "./tokenProvider";

function parseTenantEnv(data: any) {
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch (err) {
      console.error("Invalid JSON provided:", err);
      return null;
    }
  } else {
    return data;
  }
}

function extractTenantFromMapping(data, url) {
  const configObj = parseTenantEnv(data);

  // Iterate over each key in the config object
  for (const key in configObj) {
    if (Object.prototype.hasOwnProperty.call(configObj, key)) {
      const patterns = configObj[key];
      if (Array.isArray(patterns)) {
        for (const pattern of patterns) {
          // If pattern is an empty string, skip it
          if (!pattern) continue;

          try {
            const regex = new RegExp(pattern);
            if (regex.test(url)) {
              return key; // Return the key where we found a match
            }
          } catch (regexError) {
            // If pattern isn't a valid regex, handle gracefully or log
            console.warn(`Invalid regex pattern "${pattern}" for key "${key}":`, regexError);
          }
        }
      }
    }
  }

  // No matches found
  return null;
}

export const getTenant = (() => {
  let cachedTenant: string | null | undefined = null;

  return function (cookies = {}) {
    if (cachedTenant) {
      return cachedTenant;
    }

    // If logged in use tenant from jwt
    const token = getApiToken();
    if (token) {
      cachedTenant = (token as any)?.tenant;
      if (cachedTenant) {
        return cachedTenant;
      }
    }

    // If not logged in check query param first
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const tenant = urlParams.get("tenant");
    if (tenant) {
      cachedTenant = tenant;
      return cachedTenant;
    }

    // Remove 'www.' prefix from hostname to normalize it and simply TENANT_MAPPING values
    const hostname = window.location.hostname.replace(/^www\./, "");

    const tenantMapping = import.meta.env.VITE_TENANT_MAPPING;
    const matchingTenant = extractTenantFromMapping(tenantMapping, hostname);
    if (matchingTenant) {
      cachedTenant = matchingTenant;
      return cachedTenant;
    }

    // If not logged in check hostname
    const parts = hostname.split(".");
    if (parts.length > 2) {
      cachedTenant = parts.slice(-2, -1)[0]; // e.g., 'pages' for frontend-9an.pages.dev
    } else {
      cachedTenant = parts[0]; // e.g., 'example' for 'example.com'
    }

    if (cachedTenant === "localhost") {
      cachedTenant = import.meta.env.VITE_DEFAULT_TENANT;
    }

    if (hostname.endsWith("pages.dev")) {
      cachedTenant = import.meta.env.VITE_DEFAULT_TENANT;
    }

    return cachedTenant;
  };
})();

export interface AnalyticsConfig {
  [key: string]: {
    IsEnabled: boolean;
    TagId?: string | null;
    Stream?: string | null;
    StreamId?: string | null;
  };
}

type TenantConfig = {
  appleId?: string | null;
  androidId?: string | null;
  title?: string | null;
  maintenanceModeUrl?: string | null;
  analytics?: AnalyticsConfig;
  finappsWebComponentUrl?: string | null;
  forceUpgradeUrl?: boolean;
  forgotPasswordUrl?: string | null;
  publicToken?: string | null;
};

export const getTenantConfig = (() => {
  let cachedTenantConfig: TenantConfig = {};

  return function (cookies = {}) {
    if (Object.keys(cachedTenantConfig).length > 0) {
      return cachedTenantConfig;
    }
    // get tenant
    const tenant = getTenant(cookies);
    // get tenant configs in env
    const tenantMappingConfigEnv = import.meta.env.VITE_TENANT_CONFIG;
    // parse tenant mapping configs
    const tenantMap = parseTenantEnv(tenantMappingConfigEnv);

    if (tenantMap && tenant) {
      const config = tenantMap[tenant] || {};
      const baseConfig = tenantMap["base"] || {};
      if (config) {
        cachedTenantConfig = {
          ...baseConfig,
          ...config,
        };
      }
    }
    // check query params
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    // check for appleId and androidId in query params
    // if they exist, override the cached values
    const appleId = urlParams.get("appleId");
    if (appleId) cachedTenantConfig.appleId = appleId;

    const androidId = urlParams.get("androidId");
    if (androidId) cachedTenantConfig.androidId = androidId;

    return cachedTenantConfig;
  };
})();

export const getTenants = () => {
  const tenantMapping = import.meta.env.VITE_TENANT_MAPPING;
  const configObj = parseTenantEnv(tenantMapping);
  if (configObj) {
    return Object.keys(configObj);
  }
  return [];
};

export async function getProjectTenantConfig(context: RuntimeContext) {
  const { global } = getState(context);
  const tenant = getTenant();
  const ProjectTenantConfig = await callServiceLight(
    { Name: "project.tenant-config.get", Service: "project.tenant-config.get" },
    { Id: tenant },
    context
  );
  if (!ProjectTenantConfig?.success) {
    return null;
  }
  global["TenantConfig"].value = ProjectTenantConfig?.data?.Entity;
  return ProjectTenantConfig?.data?.Entity;
}
